import {
  getLLMClient,
  LLMUnavailableError,
  type LLMClient,
} from "@engine/llm/LLMClient.js";
import { SKILL_NAMES, type SkillName } from "@engine/types/entity.js";

/**
 * Character-background integration.
 *
 * Spec: `specs/llm-integrations.md §Character background generation`.
 *
 * Flow:
 *  1. Player writes a few sentences about their character in the creator UI.
 *  2. We combine race, class, and that prose into a locked-down JSON prompt
 *     and ask the LLM for a structured background (`GeneratedBackground`).
 *  3. The response is JSON-parsed, validated against an allow-list of
 *     `SkillName`s (5e SRD), and retried up to `DEFAULT_MAX_ATTEMPTS - 1`
 *     times if it comes back malformed.
 *  4. Invalid shape after all retries → caller receives `null` and falls
 *     back to an authored list (L05).
 *
 * The validator is deliberately strict: only the documented SRD skills are
 * accepted, no duplicate proficiencies, and the prose fields must be
 * non-empty strings. This keeps downstream code from coping with creative
 * hallucinations (`"proficiency in Cooking"`) at every call-site.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface BackgroundRequest {
  readonly race: string;
  readonly characterClass: string;
  /** Free-form prose from the player. Trimmed + collapsed before prompting. */
  readonly description: string;
}

export interface GeneratedBackground {
  /** SRD background slug (`"soldier"`, `"sage"`). Derived from the prose. */
  readonly backgroundId: string;
  /** Display name the player sees. */
  readonly name: string;
  /** Personality trait — one sentence. */
  readonly trait: string;
  /** Ideal — one sentence. */
  readonly ideal: string;
  /** Bond — one sentence. */
  readonly bond: string;
  /** Flaw — one sentence. */
  readonly flaw: string;
  /** Longer backstory paragraph (3-6 sentences). */
  readonly backstory: string;
  /** Exactly two SRD skill proficiencies from `SKILL_NAMES`. */
  readonly skillProficiencies: readonly [SkillName, SkillName];
  /** Starting equipment item slugs (refs into `public/data/items/`). */
  readonly startingEquipment: readonly string[];
}

// ── Tunables ─────────────────────────────────────────────────────────────────

/** Total attempts, including the first one — retries on malformed JSON. */
export const DEFAULT_MAX_ATTEMPTS = 3;

const SKILL_SET = new Set<SkillName>(SKILL_NAMES);

// ── Prompt builder ───────────────────────────────────────────────────────────

const SYSTEM_PROMPT = [
  "You are a D&D 5e character-background generator for the game bkcrawl.",
  "Produce mechanically valid, PG-13 backgrounds grounded in 5e SRD mechanics.",
  "Reply with a single JSON object and nothing else — no prose, no Markdown, no code fences.",
  "The two skill proficiencies MUST be chosen from this exact list (case sensitive):",
  SKILL_NAMES.join(", ") + ".",
  "Do not invent skills. Do not duplicate the same skill twice.",
].join("\n");

export function buildBackgroundPrompt(request: BackgroundRequest): string {
  const { race, characterClass, description } = request;
  const blurb = description.trim().replace(/\s+/g, " ");
  return [
    `Generate a background for a ${race} ${characterClass}.`,
    blurb.length > 0
      ? `Player description: ${blurb}`
      : "No player description provided — invent something evocative.",
    "",
    "Reply with JSON matching this schema:",
    "{",
    `  "backgroundId": "soldier" | "sage" | "acolyte" | "criminal" | "folk_hero" | "noble" | "outlander" | "hermit" | string,`,
    `  "name": string,`,
    `  "trait": string,`,
    `  "ideal": string,`,
    `  "bond": string,`,
    `  "flaw": string,`,
    `  "backstory": string,`,
    `  "skillProficiencies": [SkillName, SkillName],`,
    `  "startingEquipment": string[]`,
    "}",
  ].join("\n");
}

// ── Validator ────────────────────────────────────────────────────────────────

export interface ValidationOk {
  readonly ok: true;
  readonly background: GeneratedBackground;
}
export interface ValidationErr {
  readonly ok: false;
  readonly reason: string;
}
export type ValidationResult = ValidationOk | ValidationErr;

/**
 * Parse + validate a raw model reply. Returns `ok: true` only when every
 * field is well-formed — no partial backgrounds.
 */
export function parseAndValidateBackground(rawText: string): ValidationResult {
  const trimmed = rawText.trim();
  if (!trimmed) return { ok: false, reason: "empty response" };

  // Strip a leading/trailing Markdown code fence if the model ignored the rules.
  const body = stripCodeFence(trimmed);

  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return { ok: false, reason: "invalid JSON" };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, reason: "root is not an object" };
  }
  const o = parsed as Record<string, unknown>;

  for (const key of ["backgroundId", "name", "trait", "ideal", "bond", "flaw", "backstory"]) {
    const v = o[key];
    if (typeof v !== "string" || v.trim().length === 0) {
      return { ok: false, reason: `field "${key}" missing or not a non-empty string` };
    }
  }

  if (!Array.isArray(o["skillProficiencies"]) || o["skillProficiencies"].length !== 2) {
    return { ok: false, reason: "skillProficiencies must be an array of 2 names" };
  }
  const skills = o["skillProficiencies"] as unknown[];
  for (const s of skills) {
    if (typeof s !== "string" || !SKILL_SET.has(s as SkillName)) {
      return { ok: false, reason: `unknown skill "${String(s)}"` };
    }
  }
  if (skills[0] === skills[1]) {
    return { ok: false, reason: "skillProficiencies must be distinct" };
  }

  if (!Array.isArray(o["startingEquipment"])) {
    return { ok: false, reason: "startingEquipment must be an array of strings" };
  }
  const eq = o["startingEquipment"] as unknown[];
  for (const item of eq) {
    if (typeof item !== "string" || item.trim().length === 0) {
      return { ok: false, reason: "startingEquipment entries must be non-empty strings" };
    }
  }

  const bg: GeneratedBackground = {
    backgroundId: (o["backgroundId"] as string).trim(),
    name: (o["name"] as string).trim(),
    trait: (o["trait"] as string).trim(),
    ideal: (o["ideal"] as string).trim(),
    bond: (o["bond"] as string).trim(),
    flaw: (o["flaw"] as string).trim(),
    backstory: (o["backstory"] as string).trim(),
    skillProficiencies: [skills[0] as SkillName, skills[1] as SkillName] as const,
    startingEquipment: (eq as string[]).map((s) => s.trim()),
  };
  return { ok: true, background: bg };
}

// ── Generator ────────────────────────────────────────────────────────────────

export interface GenerateBackgroundOptions {
  readonly maxAttempts?: number;
  /** Override the active LLM client (tests). Defaults to `getLLMClient()`. */
  readonly client?: LLMClient;
}

/**
 * Request a background from the LLM and validate the reply. Retries on
 * malformed JSON / validation failure up to `maxAttempts` times.
 *
 * Returns `null` when:
 *  - No LLM client is configured.
 *  - Every attempt returned an invalid payload.
 *  - The client threw `LLMUnavailableError` mid-flight.
 *
 * Callers (character creator UI) should fall back to `L05` in these cases.
 */
export async function generateBackground(
  request: BackgroundRequest,
  options: GenerateBackgroundOptions = {},
): Promise<GeneratedBackground | null> {
  const client = options.client ?? getLLMClient();
  if (!client.isConfigured) return null;

  const maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);
  const prompt = buildBackgroundPrompt(request);

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let text: string;
    try {
      const response = await client.generateText({
        systemPrompt: SYSTEM_PROMPT,
        userPrompt: prompt,
        // Small temperature bump on retries encourages the model to try a
        // different shape rather than repeat the same malformed answer.
        temperature: 0.6 + (attempt - 1) * 0.1,
      });
      text = response.text;
    } catch (err) {
      if (err instanceof LLMUnavailableError) return null;
      throw err;
    }

    const result = parseAndValidateBackground(text);
    if (result.ok) return result.background;
  }

  return null;
}

// ── Internals ────────────────────────────────────────────────────────────────

function stripCodeFence(text: string): string {
  const fenced = /^```(?:json)?\s*([\s\S]*?)\s*```$/i.exec(text);
  return fenced ? fenced[1]!.trim() : text;
}
